import { Button, Col, message, Row, Spin, Tooltip } from 'antd';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import Alert from '../../../../shared/components/alert/alert';
import CustomButton from '../../../../shared/components/buttons/custom-button/custom-button';
import DocumentsDeleteAllModal from '../../../../shared/components/documents-delete-all-modal/documents-delete-all-modal';
import IconWrapper from '../../../../shared/components/icon-wrapper/icon-wrapper';
import { deleteAllDocumentsModalId } from '../../../../shared/constants/constants';
import { useBreakpoint } from '../../../../shared/hooks/use-breakpoint';
import { CopyIcon } from '../../../../shared/icons/copy-icon';
import { CreateNewVersionIcon } from '../../../../shared/icons/create-new-version-icon';
import { DocumentAddIcon } from '../../../../shared/icons/document-add-icon';
import { DocumentImagePlusIcon } from '../../../../shared/icons/document-image-plus-icon';
import { TrashIcon } from '../../../../shared/icons/trash-icon';
import { useAppSelector } from '../../../../store/hooks';
import ArchiveFileUploadPreview from '../../../archive/components/archive-file-upload-preview/archive-file-upload-preview';
import ScanRequestsPreview from '../../../scan-requests/components/scan-requests-preview/scan-requests-preview';
import { scanRequestAlertId, scanRequestFormModalId } from '../../../scan-requests/constants/constants';
import { useCreateSignRequestMutation } from '../../../sign-requests/api/sign-requests-api';
import SignRequestsPreview from '../../../sign-requests/components/sign-requests-preview/sign-requests-preview';
import {
  signRequestAlertId,
  signRequestCreateModalId,
  signRequestInPortalModalId,
} from '../../../sign-requests/constants/constants';
import { SignRequestStatus } from '../../../sign-requests/models/sign-request-status';
import { SignRequestType } from '../../../sign-requests/models/sign-request-type';
import { useLazyGetTemplateQuery } from '../../../templates/api/templates-api';
import { openModal, setAlert } from '../../../ui/store/ui-slice';
import { useGetUserQuery, useLazyGetUserIframeUrlQuery, usePurgeUserTasksMutation } from '../../../user/api/user-api';
import { useGetRequestQuery } from '../../api/request-api';
import { requestPreviewAlertId } from '../../constants/constants';
import { selectActiveRequestId } from '../../store/requests-slice';
import styles from './request-preview.module.css';
import useThrottle from '../../../../shared/hooks/use-throttle';
import { hasPermission } from '../../../../shared/utils/has-permission';
import { UserPermissionType } from '../../../user/models/user-permission-type';
import { Routes } from '../../../../app/routes';

interface Props {
  fallbackRoute?: Routes;
  signRequestId?: string;
}

const RequestPreview = ({ fallbackRoute, signRequestId }: Props): JSX.Element => {
  const { t } = useTranslation('common');
  const [copyClipboardMessageApi, copyClipboardMessage] = message.useMessage();
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const requestId = useAppSelector(selectActiveRequestId)!;
  const dispatch = useDispatch();
  const { data: currentUser } = useGetUserQuery();
  const [purgeUserTasks, { isLoading: isPurgeUserTasksLoading }] = usePurgeUserTasksMutation();
  const [createSignRequest, { isLoading: isCreateSignRequestLoading }] = useCreateSignRequestMutation();
  const [getUserIframeUrl] = useLazyGetUserIframeUrlQuery();
  const [getTemplate, { isFetching: isTemplateFetching }] = useLazyGetTemplateQuery();
  const screens = useBreakpoint();
  const throttle = useThrottle();
  const [isSignRequestValid, setIsSignRequestValid] = useState(true);

  const { currentData: request, isFetching: isRequestFetching } = useGetRequestQuery(requestId, {
    skip: !requestId,
  });

  const copyRequestShortId = async () => {
    if (request?.request.shortId) {
      await navigator.clipboard.writeText(request?.request.shortId || '');

      copyClipboardMessageApi.open({
        type: 'success',
        content: t('tablePreviewContent.messages.shortIdCopied'),
      });
    }
  };

  const openDeleteAllDocumentsModal = () => {
    dispatch(openModal({ id: deleteAllDocumentsModalId, props: { fallbackRoute } }));
  };

  const openScanDocumentModal = () => {
    dispatch(
      openModal({
        id: scanRequestFormModalId,
        props: { requestId: requestId },
      }),
    );
  };

  const isAllDocumentsDeleted = useMemo(() => {
    if (request) {
      const allDocuments = [...request.scanRequests, ...request.signRequests];
      return allDocuments.every(document => document.deleted);
    }
  }, [request]);

  const setSignRequestCreateAlertMessage = (alertId: string) => {
    dispatch(
      setAlert({
        id: alertId,
        type: 'error',
        title: t('errors.unexpected'),
      }),
    );
  };

  const signRequest = useMemo(
    () =>
      request?.signRequests &&
      request?.signRequests.find(
        signRequest => signRequest.status === SignRequestStatus.Signed && signRequest.template,
      ),
    [request?.signRequests],
  );

  useEffect(() => {
    if (!signRequest) {
      setIsSignRequestValid(false);
    } else {
      setIsSignRequestValid(true);
    }
  }, [signRequest]);

  const createNewVersion = async () => {
    const template = signRequest?.template?.deleted
      ? await getTemplate(signRequest.template.code)
          .unwrap()
          .catch(() => null)
      : signRequest?.template;

    const payload = {
      signRequest: {
        type: SignRequestType.User,
        clientUser: currentUser?.id,
        ...(signRequest?.template && { template: template?.id || '' }),
        ...(requestId && { request: requestId }),
      },
      copyMetadata: true,
      copyContent: true,
      hideOldFields: true,
      relatedSignRequest: signRequest?.id,
    };

    await purgeUserTasks();

    createSignRequest(payload)
      .unwrap()
      .then(() => {
        getUserIframeUrl(undefined, false)
          .unwrap()
          .then(iframeUrl => {
            dispatch(openModal({ id: signRequestInPortalModalId, props: { iframeUrl } }));
          });
      })
      .catch(() => {
        setSignRequestCreateAlertMessage(signRequestAlertId);
        setSignRequestCreateAlertMessage(requestPreviewAlertId);
        setSignRequestCreateAlertMessage(scanRequestAlertId);
      });
  };
  const lastActionBtnRef = useRef<HTMLDivElement>(null);

  const handleLastButtonPosition = () => {
    if (lastActionBtnRef.current) {
      const element = lastActionBtnRef.current;
      const previousSibling = element.previousElementSibling as HTMLDivElement;
      if (previousSibling) {
        const previousTop = previousSibling.getBoundingClientRect().top;
        const currentTop = element.getBoundingClientRect().top;

        if (currentTop > previousTop) {
          element.style.marginLeft = '0';
          element.style.marginRight = 'auto';
        } else {
          element.style.marginLeft = 'auto';
          element.style.marginRight = '0';
        }
      }
    }
  };

  useEffect(() => {
    handleLastButtonPosition();
    const throttledResizeHandler = () => throttle(handleLastButtonPosition);
    window.addEventListener('resize', throttledResizeHandler);

    return () => window.removeEventListener('resize', throttledResizeHandler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.previewContentContainer}>
      <Alert alertId={requestPreviewAlertId} />
      <div className={styles.previewContent}>
        {copyClipboardMessage}
        <Spin spinning={isRequestFetching}>
          <Row gutter={[8, 8]} className={styles.row} align="middle">
            <Col xs={24} sm={8}>
              <span className={styles.requestText}>{t('tablePreviewContent.requestId')} </span>
              <span>{request?.request.shortId}</span>
              <span>
                <Button
                  onClick={copyRequestShortId}
                  type="text"
                  className={styles.copyIcon}
                  size="small"
                  shape="circle"
                  icon={
                    <IconWrapper>
                      <CopyIcon />
                    </IconWrapper>
                  }
                />
              </span>
            </Col>
            <Col xs={24} sm={8}>
              <span className={styles.requestText}>{t('tablePreviewContent.relatedSignRequestId')} </span>
              <span>{request?.request.signCount}</span>
            </Col>

            <Col xs={24} sm={8}>
              <span className={styles.requestText}>{t('tablePreviewContent.relatedScanRequests')} </span>
              <span>{request?.request.scanCount}</span>
            </Col>
          </Row>
          {!screens.xs && (
            <Row gutter={[8, 8]} className={styles.globalButtonContainer}>
              <Col>
                <CustomButton
                  onClick={openScanDocumentModal}
                  size="small"
                  type="primary"
                  fontWeight={400}
                  fontSize={12}
                  icon={<DocumentImagePlusIcon />}
                  disabled={isAllDocumentsDeleted}
                >
                  {t('signScanRequest.action.scanDoc')}
                </CustomButton>
              </Col>
              <Col>
                <CustomButton
                  onClick={() => {
                    dispatch(
                      openModal({
                        id: signRequestCreateModalId,
                        props: { requestId: request?.request.id, signRequestId: signRequestId },
                      }),
                    );
                  }}
                  size="small"
                  type="primary"
                  fontWeight={400}
                  fontSize={12}
                  icon={<DocumentAddIcon />}
                  disabled={isAllDocumentsDeleted}
                >
                  {t('signScanRequest.action.additionalDocument')}
                </CustomButton>
              </Col>
              {request?.signRequests && request.signRequests.length > 0 && (
                <Col>
                  <CustomButton
                    onClick={createNewVersion}
                    size="small"
                    type="primary"
                    fontWeight={400}
                    fontSize={12}
                    icon={<CreateNewVersionIcon />}
                    disabled={
                      isAllDocumentsDeleted ||
                      isCreateSignRequestLoading ||
                      isPurgeUserTasksLoading ||
                      !isSignRequestValid
                    }
                    loading={isCreateSignRequestLoading || isPurgeUserTasksLoading || isTemplateFetching}
                  >
                    {t('tablePreviewContent.action.addInfoToPdf')}
                  </CustomButton>
                </Col>
              )}
              <Col ref={lastActionBtnRef}>
                <Tooltip
                  mouseEnterDelay={0.5}
                  title={
                    currentUser &&
                    !hasPermission(currentUser, UserPermissionType.DeleteDocuments) &&
                    t('hint.noPermissionDeleteDocument')
                  }
                >
                  <CustomButton
                    onClick={openDeleteAllDocumentsModal}
                    size="small"
                    danger
                    type="default"
                    fontWeight={400}
                    fontSize={12}
                    icon={<TrashIcon />}
                    disabled={
                      isAllDocumentsDeleted ||
                      (currentUser && !hasPermission(currentUser, UserPermissionType.DeleteDocuments))
                    }
                  >
                    {t('signScanRequest.action.deleteAll')}
                  </CustomButton>
                </Tooltip>
              </Col>
            </Row>
          )}
          {request && (
            <div className={styles.collapsableContainer}>
              <Suspense>
                {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                <SignRequestsPreview requestItem={request} currentUser={currentUser} />
              </Suspense>
              <Suspense>
                <ScanRequestsPreview request={request} currentUser={currentUser} />
              </Suspense>
              <Suspense>
                <ArchiveFileUploadPreview files={request.files} />
              </Suspense>
            </div>
          )}
        </Spin>
        <DocumentsDeleteAllModal />
      </div>
    </div>
  );
};

export default RequestPreview;
